/**
 * @file Detail-view leave confirmation and save orchestration helpers.
 */

import { state } from '../../app/state/state.js';
import { validateNativeFormFields } from '../forms/nativeFieldStyles.js';
import {
    focusFirstRegisteredControlError,
    validateRegisteredControls,
} from '../ui/controls/controlCore.js';
import { openConfirmationModal } from '../ui/confirmationModal.js';
import { clearDetailDirtyFields } from './detailDirtyState.js';
import {
    clearDetailNoteDraft,
    getDetailNoteDraft,
    hasDetailNoteDraft,
} from './noteDraftState.js';

function getModalCopy(hasUnsavedNote, hasUnsavedChanges) {
    if (hasUnsavedNote && hasUnsavedChanges) {
        return {
            title: 'Unsaved note and changes',
            message: 'You have an unsaved note and other unsaved changes. Are you sure you want to leave? Your note and changes will be lost.',
        };
    }

    if (hasUnsavedNote) {
        return {
            title: 'Unsaved note',
            message: 'You have an unsaved note. Are you sure you want to leave? Your note will be lost.',
        };
    }

    return {
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.',
    };
}

function getErrorMessage(error) {
    return (error?.code === 'internal' || !error?.message)
        ? 'Something went wrong. Please try again later.'
        : `Failed to add note: ${error.message}.`;
}

/**
 * Returns whether the active detail item has any leave-blocking draft.
 * @returns {boolean}
 */
export function hasPendingDetailLeaveDraft() {
    return Boolean(state.selectedItemId) &&
        (state.hasUnsavedChanges || hasDetailNoteDraft(state.selectedItemId));
}

/**
 * Clears detail item and note drafts before leaving without saving.
 */
export function discardPendingDetailDrafts() {
    state.hasUnsavedChanges = false;
    clearDetailDirtyFields();
    clearDetailNoteDraft(state.selectedItemId);
}

/**
 * Saves pending note and item drafts through the existing save functions.
 * @param {object} params Save dependencies.
 * @param {HTMLElement|Document} params.root Root used to find the detail form.
 * @param {Function} params.saveDetailsForm Existing item detail save function.
 * @param {Function} params.saveNoteContent Existing note content save function.
 * @returns {Promise<boolean>} False when validation fails before saving.
 */
export async function savePendingDetailDrafts({
    root = document,
    saveDetailsForm,
    saveNoteContent,
}) {
    const detailsForm = root.querySelector('#details-form');
    const shouldSaveItem = state.hasUnsavedChanges;
    const noteDraft = getDetailNoteDraft(state.selectedItemId);

    if (shouldSaveItem) {
        if (!detailsForm) {return false;}

        const nativeValid = validateNativeFormFields(detailsForm);
        const registeredValidation = validateRegisteredControls(detailsForm);
        if (!nativeValid || !registeredValidation.valid) {
            if (nativeValid) {
                focusFirstRegisteredControlError(registeredValidation);
            }
            return false;
        }
    }

    if (noteDraft.hasText) {
        try {
            await saveNoteContent({
                itemId: noteDraft.itemId,
                rawText: noteDraft.text,
            });
            clearDetailNoteDraft(noteDraft.itemId);
        } catch (error) {
            console.error('Error saving pending note draft:', error);
            openConfirmationModal(
                'Error',
                getErrorMessage(error),
                async () => { },
                false,
                'OK',
            );
            throw error;
        }
    }

    if (shouldSaveItem) {
        const didSave = await saveDetailsForm(detailsForm);
        if (!didSave) {return false;}
        clearDetailDirtyFields();
    }

    return true;
}

/**
 * Opens the shared three-action detail leave confirmation modal.
 * @param {object} params Modal callbacks.
 * @param {Function} params.onContinue Callback after discarding drafts.
 * @param {Function} params.onSave Callback that saves drafts and navigates.
 * @param {Function} [params.onStay] Optional callback when the user cancels.
 * @returns {boolean} True when a modal was opened.
 */
export function openDetailLeaveModal({ onContinue, onSave, onStay = null }) {
    const hasUnsavedNote = hasDetailNoteDraft(state.selectedItemId);
    const hasUnsavedChanges = state.hasUnsavedChanges;

    if (!state.selectedItemId || (!hasUnsavedNote && !hasUnsavedChanges)) {
        return false;
    }

    const copy = getModalCopy(hasUnsavedNote, hasUnsavedChanges);
    openConfirmationModal(
        copy.title,
        copy.message,
        onSave,
        false,
        'Save',
        onStay,
        'Saving...',
        'Cancel',
        {
            closeOnError: false,
            leftAction: {
                buttonText: 'Continue',
                danger: true,
                onClick: async () => {
                    discardPendingDetailDrafts();
                    await onContinue();
                },
            },
        },
    );
    return true;
}
